/**
 * The panel down the left, which changes with the mode.
 *
 * Only what the current mode can actually use is drawn: a terrain brush is
 * meaningless while placing scenery, and a list of trees is meaningless while
 * sculpting. Modes that put things in the world share the same browser, because
 * searching for something should work the same way wherever it is being used.
 */

import type {
  ItemDefinitionWire,
  NpcDefinitionWire,
  ObjectDefinitionWire,
} from '@inverse/protocol';
import { memo, useMemo, useState } from 'react';
import { thumbnailFor } from '../../game/thumbnails';
import { Explain } from '../Explain';
import {
  SELECT_FILTERS,
  SPAWN_KINDS,
  TERRAIN_ACTIONS,
  TERRAIN_GROUPS,
  buildingKind,
  characterCategories,
  labelFor,
  PIECE_LABELS,
  placeableCharacters,
  placeableLoot,
  placeableObjects,
  type EditorMode,
  type Placeable,
  type Piece,
  type SelectFilter,
  type SpawnKind,
  type TerrainAction,
} from './model';

/**
 * Which tier of a kind was chosen last, remembered between sessions.
 *
 * The browser shows one tile per kind with the options behind it, and the tile
 * puts down whichever option is chosen. Forgetting that choice means finding
 * flagstones again every time something else is pressed.
 */
const TIER_KEY = 'inverse.editor.tier.';

function lastTier(family: string): number {
  if (family.length === 0) return 0;

  try {
    return Number(window.localStorage.getItem(TIER_KEY + family) ?? 0);
  } catch {
    return 0;
  }
}

function rememberTier(family: string, id: number): void {
  if (family.length === 0) return;

  try {
    window.localStorage.setItem(TIER_KEY + family, String(id));
  } catch {
    // Private browsing. It still works, it just forgets.
  }
}

/** What one of the ways of working the ground is called, for saying it back. */
function labelOf(action: TerrainAction): string {
  return TERRAIN_ACTIONS.find((entry) => entry.action === action)?.label ?? 'Do it to';
}

function Palette({
  mode,
  objects,
  characters,
  items,
  lootMany,
  onLootMany,
  lootBack,
  onLootBack,
  lootEach,
  onLootEach,
  chosen,
  onChoose,
  terrain,
  onTerrain,
  onApply,
  onFixWalkable,
  slopeTop,
  onSlopeTop,
  restsOpen,
  onRestsOpen,
  held,
  brush,
  onBrush,
  strength,
  onStrength,
  roundBrush,
  onRoundBrush,
  buildStoreys,
  onBuildStoreys,
  buildFacing,
  onBuildFacing,
  buildWindows,
  onBuildWindows,
  buildFurnish,
  onBuildFurnish,
  vary,
  onVary,
  density,
  onDensity,
  spawnKind,
  onSpawnKind,
  zoneMax,
  onZoneMax,
  zoneMin,
  zoneSpan,
  onZoneTimers,
  pieces,
  onForgetPiece,
}: {
  mode: EditorMode;
  objects: readonly ObjectDefinitionWire[];
  characters: readonly NpcDefinitionWire[];
  /** Everything there is, for the ones that are left lying about. */
  items: readonly ItemDefinitionWire[];
  /** What a pile left lying about is, and what it does once it is taken. */
  lootMany: number;
  onLootMany: (many: number) => void;
  lootBack: number;
  onLootBack: (ticks: number) => void;
  lootEach: boolean;
  onLootEach: (each: boolean) => void;
  chosen: Placeable | null;
  onChoose: (placeable: Placeable) => void;
  terrain: TerrainAction;
  onTerrain: (action: TerrainAction) => void;
  /** Does one of them to whatever ground is held, rather than to what is clicked. */
  onApply: (action: TerrainAction) => void;
  /** Puts what is closed to walking back in step with what is standing there. */
  onFixWalkable: () => void;
  /** The height a ramp's high side ends up at, or null to rise by the strength. */
  slopeTop: number | null;
  onSlopeTop: (top: number | null) => void;
  /** Whether a door is built standing open, which is how it will rest. */
  restsOpen: boolean;
  onRestsOpen: (open: boolean) => void;
  /** How many tiles are held, which is what Apply would work on. */
  held: number;
  brush: number;
  onBrush: (size: number) => void;
  /** How far one stroke moves the ground, and whether the brush is round. */
  strength: number;
  onStrength: (strength: number) => void;
  roundBrush: boolean;
  onRoundBrush: (round: boolean) => void;
  /** What a building comes out as, which is kept while a row of them goes up. */
  buildStoreys: number;
  onBuildStoreys: (storeys: number) => void;
  buildFacing: number;
  onBuildFacing: (facing: number) => void;
  buildWindows: boolean;
  onBuildWindows: (windows: boolean) => void;
  buildFurnish: boolean;
  onBuildFurnish: (furnish: boolean) => void;
  /** Whether a brush of scenery turns and sizes each one differently. */
  vary: boolean;
  onVary: (vary: boolean) => void;
  /** How much of a brush of scenery actually lands, so a wood has gaps in it. */
  density: number;
  onDensity: (density: number) => void;
  /** Which half of Spawns is being worked in: people who stand, or things that come back. */
  spawnKind: SpawnKind;
  onSpawnKind: (kind: SpawnKind) => void;
  zoneMax: number;
  onZoneMax: (max: number) => void;
  zoneMin: number;
  zoneSpan: number;
  onZoneTimers: (min: number, span: number) => void;
  /** Groups kept to be built again. */
  pieces: readonly Piece[];
  onForgetPiece: (name: string) => void;
}): JSX.Element | null {
  // Worked out before anything is drawn: what is shown depends on the mode, but
  // what is known must not, or the hooks would come and go with it.
  const placeables = useMemo(() => placeableObjects(objects), [objects]);
  const buildingParts = useMemo(
    () => placeables.filter((entry) => entry.piece === true),
    [placeables],
  );
  const people = useMemo(() => placeableCharacters(characters), [characters]);
  const loot = useMemo(() => placeableLoot(items), [items]);
  const groups = useMemo(() => characterCategories(characters), [characters]);

  if (mode === 'play') return null;

  return (
    <div className="editor-body">
      {mode === 'place' || mode === 'build' ? (
        <Browser
          entries={mode === 'build' ? buildingParts : [...placeables, ...loot]}
          groups={
            mode === 'build'
              ? ['building']
              : ['scenery', 'resources', 'crafting', 'transport', 'utilities', 'loot']
          }
          chosen={chosen}
          onChoose={onChoose}
        />
      ) : null}

      {mode === 'build' && pieces.length > 0 ? (
        <>
          <h3>Pieces</h3>
          {pieces.map((piece) => (
            <div className="editor-piece" key={piece.name}>
              <button
                type="button"
                className={
                  chosen?.kind === 'piece' && chosen.name === piece.name
                    ? 'editor-entry on'
                    : 'editor-entry'
                }
                draggable
                onDragStart={(event) => {
                  const held: Placeable = {
                    kind: 'piece',
                    id: 0,
                    name: piece.name,
                    category: 'building',
                    detail: `${piece.parts.length} parts`,
                  };

                  onChoose(held);
                  event.dataTransfer.effectAllowed = 'copy';
                  event.dataTransfer.setData('text/plain', piece.name);
                  event.dataTransfer.setData('application/inverse-placeable', JSON.stringify(held));
                }}
                onClick={() =>
                  onChoose({
                    kind: 'piece',
                    id: 0,
                    name: piece.name,
                    category: 'building',
                    detail: `${piece.parts.length} parts`,
                  })
                }
              >
                <span>{piece.name}</span>
                <small>{piece.parts.length} parts</small>
              </button>
              <button
                type="button"
                className="editor-drop-remove"
                title="Forget it"
                onClick={() => onForgetPiece(piece.name)}
              >
                ✕
              </button>
            </div>
          ))}
          <p className="hint">
            Kept in this browser, not in the world.
            <Explain>They are a way of working, like a brush size.</Explain>
          </p>
        </>
      ) : null}

      {mode === 'spawns' ? (
        <>
          {/* Two jobs, two sets of questions: somebody who stands there and can
              be talked to, or something that keeps coming back inside an area. */}
          <div className="editor-tabs">
            {SPAWN_KINDS.map((entry) => (
              <button
                key={entry.kind}
                type="button"
                className={spawnKind === entry.kind ? 'editor-tab on' : 'editor-tab'}
                title={entry.hint}
                onClick={() => onSpawnKind(entry.kind)}
              >
                {entry.label}
              </button>
            ))}
          </div>

          <Browser
            entries={people.filter((entry) =>
              spawnKind === 'enemies' ? entry.fights === true : entry.fights !== true,
            )}
            groups={groups}
            chosen={chosen}
            onChoose={onChoose}
          />

          {spawnKind === 'people' ? (
            <p className="hint">
              Put down where they stand.
              <Explain>
                What they say, what they sell and what they drop are in the details pane once they
                are there.
              </Explain>
            </p>
          ) : (
            <>
              <h3>New areas</h3>
              <p className="hint">
                Putting one down draws an area that keeps it there.
                <Explain>These are the numbers that area starts with.</Explain>
              </p>
              <Slider label="At once" value={zoneMax} min={1} max={12} onChange={onZoneMax} />
              <Slider
                label="Wait"
                value={zoneMin}
                min={5}
                max={200}
                suffix=" ticks"
                onChange={(value) => onZoneTimers(value, zoneSpan)}
              />
              <Slider
                label="Then up to"
                value={zoneSpan}
                min={0}
                max={200}
                suffix=" more"
                onChange={(value) => onZoneTimers(zoneMin, value)}
              />
            </>
          )}
        </>
      ) : null}

      {mode === 'terrain' ? (
        <>
          <Slider label="Brush" value={brush} min={1} max={15} step={2} onChange={onBrush} />
          <Slider
            label="Strength"
            value={strength}
            min={0.05}
            max={1}
            step={0.05}
            onChange={onStrength}
          />

          <div className="editor-brushes">
            <button
              type="button"
              className={roundBrush ? 'editor-tab on' : 'editor-tab'}
              onClick={() => onRoundBrush(true)}
            >
              Round
            </button>
            <button
              type="button"
              className={roundBrush ? 'editor-tab' : 'editor-tab on'}
              onClick={() => onRoundBrush(false)}
            >
              Square
            </button>
          </div>
          <p className="hint">
            A round brush falls away at its edge.
            <Explain>Which is what makes a rise look like a hill rather than a step.</Explain>
          </p>
        </>
      ) : null}

      {mode === 'terrain' ? (
        <>
          <p className="hint">
            {held > 0 ? `${held} tiles held.` : 'Nothing held.'}
            <Explain>
              Mark out ground with Select or the Lasso, then press what to do to it. Pressing it
              again does it again. The Brush works under the pointer as you go, held or not.
            </Explain>
          </p>

          {TERRAIN_GROUPS.map((group) => (
            <div key={group}>
              <h3>{group}</h3>
              <div className="editor-strokes">
                {TERRAIN_ACTIONS.filter((entry) => entry.group === group).map((entry) => {
                  const lit = terrain === entry.action;
                  const wouldApply = lit && held > 0;

                  return (
                    <button
                      key={entry.action}
                      type="button"
                      className={lit ? 'editor-tab on' : 'editor-tab'}
                      // What it does to the ground, rather than a line about how
                      // the panel works: somebody hovering a landscape tool
                      // wants to know what the tool is for.
                      title={
                        wouldApply
                          ? `${entry.hint}. Press again to do it to the ${held} tiles held.`
                          : entry.hint
                      }
                      // Pressing what is already in hand does it, rather than
                      // sending somebody to the bottom of the panel for a
                      // button that says the same thing.
                      onClick={() => (wouldApply ? onApply(entry.action) : onTerrain(entry.action))}
                    >
                      {entry.label}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}

          {buildingKind(terrain) ? (
            <>
              <p className="hint">
                Mark out the floor, then press the kind again to raise it.
                <Explain>
                  It lays the same pieces the Place tool has: a floor on every tile, a wall on every
                  outside edge, a roof over the top and a door in the side you choose. Take it apart
                  and change it afterwards like anything else.
                </Explain>
              </p>

              <Slider
                label={buildStoreys === 0 ? 'Storeys: as the kind' : 'Storeys'}
                value={buildStoreys}
                min={0}
                max={3}
                step={1}
                onChange={onBuildStoreys}
              />

              <div className="editor-brushes">
                {(
                  [
                    [0, 'Door south'],
                    [1, 'Door east'],
                    [2, 'Door north'],
                    [3, 'Door west'],
                  ] as const
                ).map(([side, label]) => (
                  <button
                    key={side}
                    type="button"
                    className={buildFacing === side ? 'editor-tab on' : 'editor-tab'}
                    onClick={() => onBuildFacing(side)}
                  >
                    {label}
                  </button>
                ))}
              </div>

              <div className="editor-brushes">
                <button
                  type="button"
                  className={buildWindows ? 'editor-tab on' : 'editor-tab'}
                  title="Put windows in the walls"
                  onClick={() => onBuildWindows(!buildWindows)}
                >
                  Windows
                </button>
                <button
                  type="button"
                  className={buildFurnish ? 'editor-tab on' : 'editor-tab'}
                  title="Put a hearth, a table, a stool and a bed on the ground floor"
                  onClick={() => onBuildFurnish(!buildFurnish)}
                >
                  Furnish
                </button>
              </div>
            </>
          ) : null}

          {terrain === 'slope' ? (
            <>
              <p className="hint">
                A ramp runs the way you drew it: the foot is where the stroke began.
                <Explain>
                  Drag out a box or draw with the Brush, and the low side is the end you started
                  from. The foot stays flush with the ground beside it.
                </Explain>
              </p>
              <label className="editor-field">
                <span>
                  Top of the ramp
                  <Explain>
                    The height its high side ends up at. Left empty it rises by the strength
                    instead, which is a ramp of no particular height.
                  </Explain>
                </span>
                <input
                  type="number"
                  step={0.5}
                  placeholder="by the strength"
                  value={slopeTop ?? ''}
                  onChange={(event) =>
                    onSlopeTop(event.target.value === '' ? null : Number(event.target.value))
                  }
                />
              </label>
            </>
          ) : null}

          {/* Still here as well as the second press: somebody who has just
              marked out ground wants to see that there is something to press,
              not to guess that the lit button has grown a second meaning. */}
          <button
            type="button"
            className="wide primary"
            disabled={held === 0}
            title={held === 0 ? 'Nothing is held to work on' : undefined}
            onClick={() => onApply(terrain)}
          >
            {held === 0 ? 'Nothing held to work on' : `${labelOf(terrain)} the ${held} tiles held`}
          </button>

          {/* What is closed to walking is invisible from the ground and drifts
              out of step with what is standing there: painting a tile clear
              wipes what a tree was contributing, and painting one blocked
              outlives whatever it was blocked for. */}
          <h3>Walking</h3>
          <button
            type="button"
            className="wide"
            disabled={held === 0}
            title={
              held === 0
                ? 'Mark out some ground first'
                : 'Open anything closed by hand and close whatever is standing there. Water is left as it is.'
            }
            onClick={onFixWalkable}
          >
            Fix Walkability
          </button>
        </>
      ) : null}

      {/* How a door sits once it is built. Beside the door rather than in the
          browser twice: open and shut are two states of one thing. */}
      {(chosen?.opensTo ?? 0) > 0 ? (
        <>
          <h3>{chosen?.name ?? 'It'} rests</h3>
          <div className="editor-brushes">
            {(
              [
                [false, 'Shut'],
                [true, 'Open'],
              ] as const
            ).map(([open, label]) => (
              <button
                key={label}
                type="button"
                className={restsOpen === open ? 'editor-tab on' : 'editor-tab'}
                onClick={() => onRestsOpen(open)}
              >
                {label}
              </button>
            ))}
          </div>
          <p className="hint">
            Anybody can swing it either way.
            <Explain>
              It comes back to how it rests a little while after they have walked on, so a world is
              not left standing wide open behind everybody who has been through it.
            </Explain>
          </p>
        </>
      ) : null}

      {/* What a pile left lying about does once somebody has taken it. Shown
          only while one is chosen, because none of it means anything about a
          tree or a wall. */}
      {chosen?.kind === 'loot' ? (
        <>
          <h3>Left lying</h3>
          <Slider label="How many" value={lootMany} min={1} max={100} step={1} onChange={onLootMany} />
          <Slider
            label={lootBack === 0 ? 'Gone for good' : 'Back after'}
            value={lootBack}
            min={0}
            max={6000}
            step={50}
            onChange={onLootBack}
          />
          <div className="editor-brushes">
            <button
              type="button"
              className={lootEach ? 'editor-tab on' : 'editor-tab'}
              title={
                lootEach
                  ? "Each person's own: one taking it leaves it there for everybody else"
                  : 'One for the world: the first one there takes it and it is gone for all'
              }
              onClick={() => onLootEach(!lootEach)}
            >
              {lootEach ? 'For each person' : 'For the world'}
            </button>
          </div>
          <p className="hint">
            Put down where you click, and it stays there.
            <Explain>
              Dropped loot belongs to whoever dropped it for a while, then to anybody, then to
              nobody. This is furniture: it does not rot, it comes back, and the hang above puts it
              on a table rather than on the floor under one.
            </Explain>
          </p>
        </>
      ) : null}

      {mode === 'place' || mode === 'spawns' ? (
        <Slider label="Brush" value={brush} min={1} max={15} step={2} onChange={onBrush} />
      ) : null}

      {mode === 'place' && brush > 1 ? (
        <>
          <Slider
            label="How much lands"
            value={Math.round(density * 100)}
            min={5}
            max={100}
            suffix="%"
            onChange={(percent) => onDensity(percent / 100)}
          />
          <div className="editor-brushes">
            <button
              type="button"
              className={vary ? 'editor-tab on' : 'editor-tab'}
              onClick={() => onVary(!vary)}
            >
              Vary each one
            </button>
          </div>
          <p className="hint">
            Turns each one and changes its size a little.
            <Explain>
              A wood planted from a brush should not look like a grid of the same tree.
            </Explain>
          </p>
        </>
      ) : null}
    </div>
  );
}

/**
 * Redrawn only when what it is showing changes.
 *
 * The world above it moves constantly: what the pointer is over, where the
 * character is, what the server just said. None of that is the palette's
 * business, and a search box that is redrawn mid keystroke loses letters.
 */
export const EditorPalette = memo(Palette);

/**
 * The searchable list of everything that can be put in the world.
 *
 * Grouped rather than filtered by a single category, so a search that crosses
 * two drawers still shows both, with a heading over each.
 */
function Browser({
  entries,
  groups,
  chosen,
  onChoose,
}: {
  entries: readonly Placeable[];
  groups: readonly string[];
  chosen: Placeable | null;
  onChoose: (placeable: Placeable) => void;
}): JSX.Element {
  const [search, setSearch] = useState('');
  const wanted = search.trim().toLowerCase();

  const shown = entries.filter(
    (entry) => wanted.length === 0 || entry.name.toLowerCase().includes(wanted),
  );

  return (
    <>
      <input
        className="editor-search"
        type="text"
        value={search}
        placeholder="Search"
        onChange={(event) => setSearch(event.target.value)}
      />

      {groups.map((group) => {
        const inGroup = shown.filter((entry) => entry.category === group);
        if (inGroup.length === 0) return null;

        // Anything that comes in tiers is one tile with its tiers behind a
        // chevron: a tree is a tree, and which wood it is comes after that.
        const families = new Map<string, Placeable[]>();
        for (const entry of inGroup) {
          if (!entry.family) continue;
          families.set(entry.family, [...(families.get(entry.family) ?? []), entry]);
        }

        // One tile per family, and one for everything that is only itself. The
        // tile shown for a family is whichever of its tiers is chosen, so the
        // browser says what a click would put down.
        const tiles = inGroup.filter((entry) => {
          if (!entry.family) return true;

          const tiers = families.get(entry.family) ?? [];
          const held = tiers.find((tier) => chosen?.kind === 'object' && chosen.id === tier.id);

          // Whichever tier is chosen now, then whichever was chosen last time,
          // then the first. Somebody laying flagstones all afternoon should not
          // have to find flagstones again every time they press something else.
          const before = tiers.find((tier) => tier.id === lastTier(entry.family ?? ''));

          return entry.id === (held ?? before ?? tiers[0])?.id;
        });

        return (
          <div key={group}>
            <h3>{labelFor(group === 'scenery' ? 'scenery' : group)}</h3>
            <div className="editor-grid">
              {tiles.map((entry) => (
                <Tile
                  key={`${entry.kind}-${entry.id}`}
                  entry={entry}
                  // A building piece is shown as the kind of piece it is, with
                  // the options behind it: Flooring, then boards or flags.
                  label={entry.piece ? PIECE_LABELS[entry.family ?? ''] : undefined}
                  tiers={entry.family ? (families.get(entry.family) ?? []) : []}
                  chosen={chosen}
                  onChoose={onChoose}
                />
              ))}
            </div>
          </div>
        );
      })}

      {shown.length === 0 ? <p className="hint">Nothing here.</p> : null}
    </>
  );
}

/**
 * One thing in the browser, with its tiers behind a chevron.
 *
 * A tree is one tile whichever wood it is: the tile puts down whichever tier is
 * chosen, and the chevron beside its name opens the rest of them. A right click
 * anywhere on the tile opens the same list, which is where somebody who has
 * used a browser like this before will look for it.
 */
function Tile({
  entry,
  label,
  tiers,
  chosen,
  onChoose,
}: {
  entry: Placeable;
  /**
   * What the tile is called, when that is not the name of the thing on it.
   *
   * A building piece is one entry for the kind of piece, so the tile says
   * Flooring and the small print underneath says which flooring it is.
   */
  label?: string;
  /** Every tier of this kind of thing, or nothing for something that is only itself. */
  tiers: readonly Placeable[];
  chosen: Placeable | null;
  onChoose: (placeable: Placeable) => void;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const many = tiers.length > 1;

  return (
    <div
      className="editor-tile"
      onContextMenu={(event) => {
        if (!many) return;
        event.preventDefault();
        setOpen(true);
      }}
    >
      <button
        type="button"
        className={
          chosen?.kind === entry.kind && chosen.id === entry.id ? 'editor-entry on' : 'editor-entry'
        }
        // Dragged into the world as well as clicked. Choosing it on the way out
        // means the drop and a click afterwards do the same thing, which is what
        // somebody who dragged one tree and then clicked four more expects.
        title={
          label
            ? `${label}: ${entry.name}`
            : entry.detail
              ? `${entry.name}: ${entry.detail}`
              : entry.name
        }
        draggable
        onDragStart={(event) => {
          onChoose(entry);
          event.dataTransfer.effectAllowed = 'copy';
          event.dataTransfer.setData('text/plain', entry.name);
          event.dataTransfer.setData('application/inverse-placeable', JSON.stringify(entry));
        }}
        onClick={() => {
          rememberTier(entry.family ?? '', entry.id);
          onChoose(entry);
        }}
      >
        {/* The shape itself, drawn by the same code the world uses, so a browser
            of twenty names is not twenty guesses. */}
        {entry.kind === 'object' ? (
          <Preview objectId={entry.id} looksLike={entry.looksLike ?? 0} />
        ) : (
          <span className="editor-entry-face">{entry.name.slice(0, 1)}</span>
        )}

        <span className="editor-entry-name">{label ?? entry.name}</span>
        <small>{label ? entry.name : entry.detail}</small>
      </button>

      {many ? (
        <button
          type="button"
          className="editor-tile-chevron"
          title={`${tiers.length} kinds of ${entry.family ?? 'this'}`}
          onClick={(event) => {
            event.stopPropagation();
            setOpen(!open);
          }}
        >
          ▾
        </button>
      ) : null}

      {many && open ? (
        <div className="editor-tile-tiers">
          {tiers.map((tier) => (
            <button
              key={tier.id}
              type="button"
              className={tier.id === entry.id ? 'editor-tab on' : 'editor-tab'}
              onClick={() => {
                rememberTier(tier.family ?? '', tier.id);
                onChoose(tier);
                setOpen(false);
              }}
            >
              {tier.tier || tier.name}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/** One picture of one thing, drawn once and kept. */
function Preview({ objectId, looksLike }: { objectId: number; looksLike: number }): JSX.Element {
  const source = useMemo(() => thumbnailFor(objectId, looksLike), [objectId, looksLike]);

  if (!source) return <span className="editor-entry-face">?</span>;
  return <img className="editor-entry-face" src={source} alt="" draggable={false} />;
}

export function SelectTools({
  filter,
  onFilter,
  onSelectConnected,
  selected,
}: {
  filter: SelectFilter;
  onFilter: (filter: SelectFilter) => void;
  onSelectConnected: () => void;
  selected: number;
}): JSX.Element {
  return (
    <>
      <h3>What a drag takes in</h3>
      <div className="editor-brushes">
        {SELECT_FILTERS.map((entry) => (
          <button
            key={entry.filter}
            type="button"
            className={filter === entry.filter ? 'editor-tab on' : 'editor-tab'}
            onClick={() => onFilter(entry.filter)}
          >
            {entry.label}
          </button>
        ))}
      </div>

      <h3>Whole things</h3>
      <button type="button" className="wide" disabled={selected === 0} onClick={onSelectConnected}>
        Select what it is joined to
      </button>
      <p className="hint">
        Follows walls, floors and fences.
        <Explain>From whatever is selected, so a building comes up as one thing.</Explain>
      </p>

      <p className="hint">
        Hold Ctrl to add to a selection or take something back out of it. Escape puts it all down.
      </p>
    </>
  );
}

export function Slider({
  label,
  value,
  min,
  max,
  step = 1,
  suffix = '',
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step?: number;
  suffix?: string;
  onChange: (value: number) => void;
}): JSX.Element {
  return (
    <div className="setting">
      <span>{label}</span>
      <span className="setting-slider">
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={(event) => onChange(Number(event.target.value))}
        />
        <span className="setting-value">
          {value}
          {suffix}
        </span>
      </span>
    </div>
  );
}
